'use client'

import {
  Box,
  Container,
  Heading,
  Show,
  Stack,
  Text,
  useRadioGroup,
} from '@chakra-ui/react';
import React, { useRef, useState } from 'react';
import { Keyboard, Mousewheel, Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import ColorPicker from '@/app/_components/color-picker/ColorPicker';
import CustomImage from '@/app/_components/custom-image/CustomImage';
import { getImageLink } from '@/services/link.service';
import {
  NewVehicleColor,
  NewVehicleColorsDetails,
} from '@/types/new-cars/new-cars.types';

import 'swiper/css';
import 'swiper/css/virtual';

import Styles from './ColorSection.module.scss';

type Props = {
  colour: NewVehicleColor[];
};

const defaultColorDetails: NewVehicleColorsDetails = {
  mmcode: '',
  id: 0,
  status: 'unknown',
  sort: null,
  image: '',
  color_name: 'Unknown',
  hex: '#000000',
  image_no_background_side: null,
  image_no_background_left_angle: null,
  image_alt_tag: null,
  hex_2: '#000000',
  additional_cost: null,
  is_two_tone: false,
  is_metallic: false,
};

const ColorSection = ({ colour }: Props) => {
  const swiperRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const initialColour = colour[0]?.cms_new_vehicles_colours_id;
  const [selectedColor, setSelectedColor] = useState<NewVehicleColorsDetails>(
    initialColour ? initialColour : defaultColorDetails,
  );

  const handleChange = (id: string) => {
    const newSelectedColor = colour
      .flatMap((c) => c.cms_new_vehicles_colours_id)
      .find((c) => c?.color_name.toString() === id);

    if (newSelectedColor) {
      setSelectedColor(newSelectedColor);
    }
  };

  const { getRadioProps, getRootProps } = useRadioGroup({
    defaultValue: selectedColor?.color_name,
    onChange: handleChange,
  });

  const allColors = colour.flatMap((c) => c.cms_new_vehicles_colours_id);

  return (
    <Box className={Styles.colorSectionWrapper}>
      <Container maxW={'1280'} width={'100%'}>
        <Box className={Styles.headingElement} marginBottom={12}>
          <Text fontSize={'xs'} marginBottom={2} textTransform={'uppercase'}>
            TAILOR-MADE
          </Text>
          <Heading
            as="h2"
            className={Styles.titleLg}
            textTransform={'capitalize'}
          >
            Make it yours.
          </Heading>
        </Box>

        <Stack
          alignItems={'center'}
          direction={{ base: 'column-reverse', lg: 'row' }}
          w={'100%'}
        >
          <Box
            maxWidth={{ base: '100%', lg: '346px' }}
            h={'100%'}
            width={'100%'}
          >
            <Box h={'100%'} py={{ base: 0, lg: 6 }} {...getRootProps()}>
              <Swiper
                direction={'horizontal'}
                slidesPerView={7}
                spaceBetween={12}
                mousewheel={true}
                autoHeight={true}
                modules={[Navigation, Mousewheel, Keyboard]}
                keyboard={{
                  enabled: true,
                }}
                breakpoints={{
                  992: {
                    direction: 'vertical',
                  },
                }}
                watchSlidesProgress={true}
                hashNavigation={{
                  watchState: true,
                }}
                ref={swiperRef}
                className={Styles.colorSelectSwiper}
                onSlideChange={(swiper) => setActiveIndex(swiper.activeIndex)}
              >
                {allColors.map((colorDetails, index) => (
                  <SwiperSlide
                    key={index}
                    className={`slide ${
                      activeIndex <= index && index < activeIndex + 7
                        ? Styles.active
                        : ''
                    } ${activeIndex + 6 === index ? Styles.small : ''}`}
                  >
                    <ColorPicker
                      colorName={colorDetails.color_name}
                      colorPrimary={colorDetails.hex}
                      colorSecondary={
                        colorDetails.hex_2 && colorDetails.is_two_tone
                          ? colorDetails.hex_2
                          : null
                      }
                      withLable={true}
                      {...getRadioProps({ value: colorDetails.color_name })}
                      extraClass={Styles.colorBox}
                    />
                  </SwiperSlide>
                ))}
                <SwiperSlide></SwiperSlide>
              </Swiper>
            </Box>
          </Box>
          <Box
            px={{ base: 0, lg: 9 }}
            textAlign={{ base: 'left', lg: 'center' }}
            width={{ base: '100%', lg: 'auto' }}
          >
            {selectedColor && (
              <CustomImage
                height={506}
                width={814}
                imageSrc={
                  getImageLink(
                    selectedColor.image_no_background_left_angle || '',
                  ) || ''
                }
                imageKeys={{
                  desktopKey: 'new-car-colourpicker-desktop',
                  mobileKey: 'new-car-colourpicker-mobile',
                }}
                additionalParams={{
                  fit: 'inside',
                }}
                alt={selectedColor.color_name || ''}
              />
            )}
            <Show breakpoint="(max-width: 991px)">
              <Text fontSize="sm" mt="14">
                <strong>Paint:</strong> {selectedColor?.color_name}
              </Text>
            </Show>
          </Box>
        </Stack>
      </Container>
    </Box>
  );
};

export default ColorSection;
